import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Alert } from '@mui/material';

// State name mapping for the SVG IDs
const STATE_NAMES = {
  'MA': 'Massachusetts', 'MN': 'Minnesota', 'MT': 'Montana', 'ND': 'North Dakota', 'HI': 'Hawaii',
  'ID': 'Idaho', 'WA': 'Washington', 'AZ': 'Arizona', 'CA': 'California', 'CO': 'Colorado',
  'NV': 'Nevada', 'NM': 'New Mexico', 'OR': 'Oregon', 'UT': 'Utah', 'WY': 'Wyoming',
  'AR': 'Arkansas', 'IA': 'Iowa', 'KS': 'Kansas', 'MO': 'Missouri', 'NE': 'Nebraska',
  'OK': 'Oklahoma', 'SD': 'South Dakota', 'LA': 'Louisiana', 'TX': 'Texas', 'CT': 'Connecticut',
  'NH': 'New Hampshire', 'RI': 'Rhode Island', 'VT': 'Vermont', 'AL': 'Alabama', 'FL': 'Florida',
  'GA': 'Georgia', 'MS': 'Mississippi', 'SC': 'South Carolina', 'IL': 'Illinois', 'IN': 'Indiana',
  'KY': 'Kentucky', 'NC': 'North Carolina', 'OH': 'Ohio', 'TN': 'Tennessee', 'VA': 'Virginia',
  'WV': 'West Virginia', 'WI': 'Wisconsin', 'MI': 'Michigan', 'DE': 'Delaware', 'MD': 'Maryland',
  'NJ': 'New Jersey', 'NY': 'New York', 'PA': 'Pennsylvania', 'ME': 'Maine', 'AK': 'Alaska'
};

// SVG Map Component
function USMapSVG({ data, onStateClick, selectedState, hoveredState, setHoveredState, getStateColor, getStateOpacity }) {
  const [svgContent, setSvgContent] = useState('');
  const [svgError, setSvgError] = useState('');

  const loadSVG = useCallback(async () => {
    try {
      setSvgError('');
      const response = await fetch('/us.svg');
      
      if (!response.ok) {
        throw new Error(`Failed to load map: ${response.status}`);
      }
      
      const svgText = await response.text();
      
      if (!svgText || !svgText.includes('<svg')) {
        throw new Error('Invalid SVG content');
      }
      
      // Make SVG responsive by removing fixed dimensions and adding viewBox
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
      const svgElement = svgDoc.querySelector('svg');
      
      if (svgElement) {
        // Remove fixed width and height, keep viewBox for responsiveness
        svgElement.removeAttribute('width');
        svgElement.removeAttribute('height');
        svgElement.setAttribute('preserveAspectRatio', 'xMidYMid meet');
        
        // Ensure viewBox exists
        if (!svgElement.getAttribute('viewBox')) {
          svgElement.setAttribute('viewBox', '0 0 1000 589');
        }
        
        // Add responsive styling
        svgElement.style.width = '100%';
        svgElement.style.height = '100%';
        svgElement.style.maxWidth = '100%';
        svgElement.style.maxHeight = '100%';
        
        setSvgContent(new XMLSerializer().serializeToString(svgDoc));
      } else {
        setSvgContent(svgText);
      }
    } catch (error) {
      console.error('Error loading SVG:', error);
      setSvgError('Failed to load map visualization');
    }
  }, []);

  useEffect(() => {
    loadSVG();
  }, [loadSVG]);

  useEffect(() => {
    if (!svgContent) return;

    // Use setTimeout to ensure DOM is ready after dangerouslySetInnerHTML
    const timer = setTimeout(() => {
      // Apply styles and interactions to each state
      Object.keys(STATE_NAMES).forEach(stateCode => {
        const stateElement = document.getElementById(stateCode);
        if (stateElement) {
          const count = data[stateCode] || 0;
          const stateName = STATE_NAMES[stateCode] || stateCode;
          
          // Apply styling
          stateElement.style.fill = getStateColor(stateCode);
          stateElement.style.opacity = getStateOpacity(stateCode);
          stateElement.style.stroke = selectedState === stateCode ? '#1976d2' : '#666';
          stateElement.style.strokeWidth = selectedState === stateCode ? '2' : '1';
          stateElement.style.cursor = count > 0 ? 'pointer' : 'default';
          stateElement.style.transition = 'all 0.2s ease-in-out';
          
          // Add event listeners
          stateElement.onmouseenter = () => setHoveredState(stateCode);
          stateElement.onmouseleave = () => setHoveredState(null);
          stateElement.onclick = () => count > 0 && onStateClick(stateCode);
          
          // Add tooltip with state full name and account count
          stateElement.setAttribute('title', `${stateName}: ${count} account${count !== 1 ? 's' : ''}`);
          
          // Add state code text overlay if count > 0
          if (count > 0) {
            const existingText = stateElement.parentNode.querySelector(`text[data-state="${stateCode}"]`);
            if (existingText) {
              existingText.textContent = `${stateCode} (${count})`;
            } else {
              const bbox = stateElement.getBBox();
              const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
              text.setAttribute('x', bbox.x + bbox.width / 2);
              text.setAttribute('y', bbox.y + bbox.height / 2);
              text.setAttribute('text-anchor', 'middle');
              text.setAttribute('dominant-baseline', 'middle');
              text.setAttribute('font-family', 'system-ui, -apple-system, sans-serif');
              text.setAttribute('font-size', '11');
              text.setAttribute('font-weight', '700');
              text.setAttribute('fill', '#333333');
              text.setAttribute('pointer-events', 'none');
              text.setAttribute('data-state', stateCode);
              text.textContent = `${stateCode} (${count})`;
              stateElement.parentNode.appendChild(text);
            }
          }
        }
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [svgContent, data, selectedState, hoveredState, onStateClick, setHoveredState, getStateColor, getStateOpacity]);

  if (svgError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {svgError}
      </Alert>
    );
  }

  if (!svgContent) {
    return <Typography>Loading map...</Typography>;
  }

  return (
    <div 
      dangerouslySetInnerHTML={{ __html: svgContent }}
      style={{ 
        width: '100%', 
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    />
  );
}

function USMapVisualization({ data, onStateClick, selectedState, showLegend = true }) {
  const [hoveredState, setHoveredState] = useState(null);

  const getStateColor = useCallback((stateCode) => {
    const count = data[stateCode] || 0;
    if (count === 0) return '#f8f9fa';
    if (count <= 5) return '#e8f5e8';
    if (count <= 15) return '#c8e6c8';
    if (count <= 30) return '#81c784';
    return '#4caf50';
  }, [data]);

  const getStateOpacity = useCallback((stateCode) => {
    if (selectedState === stateCode) return 1;
    if (hoveredState === stateCode) return 0.8;
    return 0.7;
  }, [selectedState, hoveredState]);

  const legendItems = useMemo(() => [
    { range: '0', color: '#f8f9fa', label: 'No accounts' },
    { range: '1-5', color: '#e8f5e8', label: '1-5 accounts' },
    { range: '6-15', color: '#c8e6c8', label: '6-15 accounts' },
    { range: '16-30', color: '#81c784', label: '16-30 accounts' },
    { range: '30+', color: '#4caf50', label: '30+ accounts' }
  ], []);

  return (
    <Box sx={{ 
      position: 'relative', 
      width: '100%', 
      minHeight: { xs: 350, sm: 450, md: 550, lg: 650 },
      maxWidth: '100%'
    }}>
      {/* US Map using SVG */}
      <Box sx={{ 
        position: 'relative', 
        width: '100%', 
        height: { xs: 300, sm: 400, md: 500, lg: 600 },
        border: '2px solid #e0e0e0',
        borderRadius: 2,
        overflow: 'hidden',
        backgroundColor: '#ffffff',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        '& svg': {
          width: '100%',
          height: '100%',
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
        }
      }}>
        <USMapSVG 
          data={data}
          onStateClick={onStateClick}
          selectedState={selectedState}
          hoveredState={hoveredState}
          setHoveredState={setHoveredState}
          getStateColor={getStateColor}
          getStateOpacity={getStateOpacity}
        />
      </Box>

      {/* Legend - Only show if showLegend is true */}
      {showLegend && (
        <Box sx={{ 
          mt: 3, 
          p: 2, 
          backgroundColor: '#fafafa', 
          borderRadius: 1, 
          border: '1px solid #e0e0e0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Account Distribution:
            </Typography>
            {legendItems.map((item) => (
              <Box key={item.range} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    backgroundColor: item.color,
                    border: '1px solid #ccc',
                    borderRadius: 0.5,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                  }}
                />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  {item.range}
                </Typography>
              </Box>
            ))}
          </Box>
          
          {/* Instructions */}
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontSize: '0.8rem',
              fontStyle: 'italic'
            }}>
            💡 Hover over states to see account details • Click states with accounts for detailed information
          </Typography>
        </Box>
      )}
    </Box>
  );
}

USMapSVG.propTypes = {
  data: PropTypes.object.isRequired,
  onStateClick: PropTypes.func.isRequired,
  selectedState: PropTypes.string,
  hoveredState: PropTypes.string,
  setHoveredState: PropTypes.func.isRequired,
  getStateColor: PropTypes.func.isRequired,
  getStateOpacity: PropTypes.func.isRequired
};

USMapVisualization.propTypes = {
  data: PropTypes.object.isRequired,
  onStateClick: PropTypes.func.isRequired,
  selectedState: PropTypes.string,
  showLegend: PropTypes.bool
};

export default USMapVisualization;